import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Check, Mail, Send, Loader2 } from "lucide-react";
import { Rich } from "@/components/Rich";
import { withDefaults } from "@/lib/sections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITE_TYPES = [
  {
    id: "landing",
    title: "Landing Page",
    desc: "Una sola página enfocada en conversión",
    base: 5000,
  },
  {
    id: "multi",
    title: "Multi-página",
    desc: "Sitio corporativo con varias secciones",
    base: 8500,
  },
];

const FEATURES = [
  { id: "whatsapp", title: "WhatsApp Business", desc: "Botón flotante + mensaje", price: 500 },
  { id: "calendar", title: "Calendario de citas", desc: "Reservas en línea", price: 1200 },
  { id: "catalog", title: "Catálogo de productos", desc: "Hasta 30 productos", price: 1800 },
  { id: "blog", title: "Blog con SEO", desc: "Sistema editorial", price: 1000 },
  { id: "maps", title: "Optimización Maps", desc: "Ficha + reseñas", price: 1500 },
];

export default function Quoter({ content }) {
  const c = withDefaults("quoter", content);
  const [siteType, setSiteType] = useState("landing");
  const [features, setFeatures] = useState([]);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const total = useMemo(() => {
    const base = SITE_TYPES.find((s) => s.id === siteType)?.base || 0;
    const extras = FEATURES.filter((f) => features.includes(f.id)).reduce(
      (sum, f) => sum + f.price,
      0
    );
    return base + extras;
  }, [siteType, features]);

  const toggleFeature = (id) => {
    setFeatures((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("El correo electrónico es obligatorio.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        site_type: siteType,
        features,
        total,
        email,
        company: company || null,
      });
      setSubmitted(true);
      toast.success("¡Cotización enviada! Te contactamos en breve.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "No se pudo enviar la cotización.";
      toast.error(typeof msg === "string" ? msg : "Error al enviar.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatMXN = (n) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <section
      id="cotizador"
      data-testid="section-quoter"
      className="relative py-20 sm:py-28 border-t border-slate-900"
    >
      <div className="absolute inset-0 tech-grid opacity-30 pointer-events-none" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="font-mono text-xs text-orange-400 mb-3">
            {c.eyebrow}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            <Rich text={c.title} />
          </h2>
          <p className="mt-4 text-slate-400 text-lg">
            {c.subtitle}
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="quoter-form"
            className="lg:col-span-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-8"
          >
            {/* Site type */}
            <div>
              <label className="font-mono text-xs text-sky-400">
                01 / Tipo de sitio
              </label>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {SITE_TYPES.map((s) => {
                  const active = siteType === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSiteType(s.id)}
                      data-testid={`quoter-site-${s.id}`}
                      className={`text-left p-4 rounded-xl border transition-all ${
                        active
                          ? "border-orange-500/60 bg-orange-500/5 shadow-[0_0_20px_-8px_rgba(249,115,22,0.5)]"
                          : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold">
                          {s.title}
                        </span>
                        <span
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            active
                              ? "border-orange-500 bg-orange-500"
                              : "border-slate-600"
                          }`}
                        >
                          {active && <Check size={12} className="text-white" />}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{s.desc}</p>
                      <p className="font-mono text-xs text-sky-400 mt-2">
                        desde {formatMXN(s.base)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Features */}
            <div>
              <label className="font-mono text-xs text-sky-400">
                02 / Funcionalidades extra
              </label>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => {
                  const active = features.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleFeature(f.id)}
                      data-testid={`quoter-feature-${f.id}`}
                      className={`text-left p-4 rounded-xl border transition-all ${
                        active
                          ? "border-sky-500/60 bg-sky-500/5"
                          : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">
                          {f.title}
                        </span>
                        <span
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            active
                              ? "border-sky-500 bg-sky-500"
                              : "border-slate-600"
                          }`}
                        >
                          {active && <Check size={12} className="text-white" />}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{f.desc}</p>
                      <p className="font-mono text-xs text-orange-400 mt-2">
                        + {formatMXN(f.price)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <label className="font-mono text-xs text-sky-400">
                03 / Tus datos
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    data-testid="quoter-input-company"
                    placeholder="Nombre del negocio (opcional)"
                    className="w-full px-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20"
                  />
                </div>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="quoter-input-email"
                    placeholder="tu@correo.com *"
                    className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                * Te enviaremos la cotización detallada y un plan de trabajo.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting || submitted}
              data-testid="quoter-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_0_25px_rgba(249,115,22,0.35)] hover:shadow-[0_0_35px_rgba(249,115,22,0.55)]"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Enviando...
                </>
              ) : submitted ? (
                <>
                  <Check size={18} /> Cotización recibida
                </>
              ) : (
                <>
                  <Send size={18} /> Enviar cotización a mi correo
                </>
              )}
            </button>
          </form>

          {/* Total card */}
          <aside
            data-testid="quoter-summary"
            className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-6 sm:p-8 lg:sticky lg:top-24 self-start"
          >
            <div className="flex items-center gap-2 text-orange-400 font-mono text-xs">
              <Calculator size={14} /> // presupuesto.estimado
            </div>
            <div className="mt-3">
              <div className="text-slate-400 text-sm">Total estimado</div>
              <div
                className="mt-1 font-mono text-4xl sm:text-5xl font-bold text-white"
                data-testid="quoter-total"
              >
                {formatMXN(total)}
              </div>
              <div className="text-xs text-slate-500 mt-1">MXN · IVA incluido</div>
            </div>

            <div className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>Base ({SITE_TYPES.find((s) => s.id === siteType)?.title})</span>
                <span className="text-slate-200 font-mono">
                  {formatMXN(SITE_TYPES.find((s) => s.id === siteType)?.base || 0)}
                </span>
              </div>
              {FEATURES.filter((f) => features.includes(f.id)).map((f) => (
                <div
                  key={f.id}
                  className="flex justify-between text-slate-400"
                  data-testid={`quoter-summary-${f.id}`}
                >
                  <span>+ {f.title}</span>
                  <span className="text-slate-200 font-mono">
                    {formatMXN(f.price)}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-800 space-y-2 text-xs text-slate-500">
              <p>· Entrega entre 7 y 21 días hábiles.</p>
              <p>· Hosting y dominio del primer año incluidos.</p>
              <p>· 30 días de soporte post-lanzamiento.</p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
